<template>
  <main id="page-poll-form" class="news-page">

    <PollPreview v-if="showPreview" :poll="form" :is-edit="!!$route.params.id" :submitting="submitting" @publish="submit" @close="closePreview" />

    <div v-show="!showPreview">
      <!-- ── Masthead compact ───────────────────── -->
      <header class="masthead masthead--compact">
        <div class="masthead__rule masthead__rule--thick"></div>
        <div class="masthead__inner masthead__inner--row">
          <button type="button" class="poll-show__back-btn" @click="$router.push({ name: 'home' })">
            ← Înapoi
          </button>
        </div>
        <div class="masthead__rule masthead__rule--thin"></div>
      </header>

      <div class="poll-form__body">
        <h1 class="poll-form__title">{{ $route.params.id ? 'Editează sondaj' : 'Adaugă sondaj nou' }}</h1>

        <div v-if="!$route.params.id && dailyLimitReached && dailyLimit !== null" class="poll-form__limit-notice">
          Ai atins limita de {{ dailyLimit }} sondaje pe zi. Poți adăuga un nou sondaj mâine.
        </div>

        <div v-if="errorMessage" class="poll-form__error">{{ errorMessage }}</div>

        <div class="poll-form__card">
          <form @submit.prevent="openPreview">

            <!-- ── Detalii principale ─────────────── -->
            <div class="poll-form__section">
              <span class="poll-form__section-label">Detalii sondaj</span>

              <LiveInputValidator :url="slugCheckUrl" param="title" :value="form.title" validatingMessage="Se verifică disponibilitatea titlului...">
                <FormField v-model="form.title" label="Titlu *" placeholder="Ex: Cu cine ai votat la ultimele alegeri?" :errors="errors['title']" />
              </LiveInputValidator>

              <FormField v-model="form.expires_at" type="date" label="Expiră la *" :errors="errors['expires_at']" />

              <FormField v-model="form.extension_days" type="select" label="Prelungiri sondaj" :options="extensionDaysOptions" :errors="errors['extension_days']" helper="Când sondajul primește un vot și îi mai rămân mai puțin de X zile până la expirare, se va prelungi automat cu numărul de zile selectat." />
            </div>

            <!-- ── Categorii & Taguri ─────────────── -->
            <div class="poll-form__section">
              <span class="poll-form__section-label">Clasificare</span>

              <TagInput v-model="form.categories" endpoint="/lookups/categories" label="Categorii *" placeholder="Caută sau adaugă categorie..." :errors="errors['categories']" />

              <div class="poll-form__spacer"></div>

              <TagInput v-model="form.tags" endpoint="/lookups/tags" label="Taguri *" placeholder="Caută sau adaugă tag..." :errors="errors['tags']" />
            </div>

            <!-- ── Opțiuni de răspuns ──────────────── -->
            <div class="poll-form__section">
              <span class="poll-form__section-label">Opțiuni de răspuns</span>

              <div v-for="(option, idx) in form.options" :key="option.id ?? option._key ?? idx" class="poll-option-card">
                <div class="poll-option-card__header">
                  <span class="poll-option-card__index">Opțiunea {{ idx + 1 }}</span>
                  <button v-if="idx >= 2 && !$route.params.id" type="button" class="poll-option-card__remove" @click="removeOption(idx)">
                    Șterge
                  </button>
                </div>

                <FormField v-model="option.title" label="Denumire *" :placeholder="'Ex: ' + defaultOptionPlaceholder(idx)" :errors="errors['options.' + idx + '.title']" />
              </div>

              <button v-if="!$route.params.id" type="button" class="poll-form__add-option" @click="addOption">
                + Adaugă opțiune răspuns
              </button>
            </div>

          </form>
        </div>
      </div>

      <!-- ── Sticky bar ─────────────────────────── -->
      <StickyFooter :show="true">
        <div class="poll-form__sticky-bar-inner">
          <button type="button" class="poll-form__publish-btn" :disabled="(!$route.params.id && dailyLimitReached) || submitting" @click="openPreview">
            Previzualizare
          </button>
        </div>
      </StickyFooter>
    </div>
  </main>
</template>

<script>
import StickyFooter from '@app/components/StickyFooter.vue';
import FormField from '@app/components/FormField.vue';
import LiveInputValidator from '@app/components/LiveInputValidator.vue';
import PollPreview from '@app/views/polls/partials/PollPreview.vue';
import TagInput from '@app/components/TagInput.vue';
import authMixin from '@app/mixins/authMixin.js';
import formable from '@app/mixins/formable.js';

let _keyCounter = 0;

function makeOption(title = '') {
  return { _key: ++_keyCounter, title };
}

export default {
  name: 'PollFormView',
  components: { FormField, LiveInputValidator, PollPreview, StickyFooter, TagInput },
  mixins: [authMixin, formable],

  data() {
    return {
      dailyLimitReached: false,
      dailyLimit: null,
      errorMessage: '',
      showPreview: false,
      extensionDaysOptions: [],
      optionPlaceholders: [],
      form: {
        title: '',
        expires_at: '',
        extension_days: 0,
        categories: [],
        tags: [],
        options: [],
      },
    };
  },

  computed: {
    slugCheckUrl() {
      const id = this.$route.params.id;
      return id ? `polls/check-slug?poll_id=${id}` : 'polls/check-slug';
    },
  },

  async mounted() {
    await this.fetchCurrentUser();
    if (!this.currentUser) {
      this.$router.push({ name: 'home' });
      return;
    }
    window.addEventListener('popstate', this.onPopState);
  },

  methods: {
    defaultOptionPlaceholder(idx) {
      return this.optionPlaceholders[idx] || 'Opțiune';
    },

    addOption() {
      this.form.options.push(makeOption());
    },

    removeOption(idx) {
      if (idx < 2) return;
      this.form.options.splice(idx, 1);
    },

    openPreview() {
      this.errorMessage = '';

      this.$api.post('polls/validate', this.form)
      .then(res => {
        history.pushState({ preview: true }, '', window.location.href);
        this.showPreview = true;
      }).catch(err => {
        if (err?.response?.status === 422) {
          this.errors = err.response.data.errors || {};
        } else {
          this.errorMessage = 'Nu am putut valida sondajul. Încearcă din nou.';
        }
      });
    },

    closePreview() {
      this.showPreview = false;
      history.back();
    },

    onPopState() {
      if (this.showPreview) {
        this.showPreview = false;
      }
    },

    onSubmitSuccess(data) {
      this.$router.replace({ name: 'polls.show', params: { id: data.poll.id, slug: data.poll.slug } });
    },

    onSubmitError(error) {
      const status = error?.response?.status;
      if (status === 403) {
        this.errorMessage = 'Fereastra de editare a expirat (60 de minute sau 5 voturi). Sondajul nu mai poate fi modificat.';
      } else if (status === 401) {
        this.errorMessage = 'Sesiunea a expirat. Te rugăm să te autentifici din nou.';
      } else if (status !== 422) {
        this.errorMessage = error?.response?.data?.message || 'Nu am putut salva sondajul. Încearcă din nou.';
      }
      this.showPreview = false;
    },
  },

  beforeUnmount() {
    window.removeEventListener('popstate', this.onPopState);
  },
};
</script>
